// Die Regeln der Übersicht: Was aus den Listen in die Spalten Positiv,
// Negativ und Fällig kommt — und was „neu" heißt.
//
// REINE FUNKTIONEN ohne Netz: Der Haken `useUebersicht` holt die Listen, die
// Vorlage im Design-System übergibt Beispiele. Beide sehen dieselben Regeln.

import type { Assessment, DueAction, MarketEvent, Money, NewsItem, Report,
              ResearchCase, TradeIdea } from '../api/client'
import type { Ref } from '../components/common/Refs'
import { artText } from '../pages/eventVocabulary'
import { label } from '../pages/researchVocabulary'
import { STATUS as IDEE_STATUS } from '../pages/tradeIdeaVocabulary'
import { nurUhr } from '../components/news/newsZeit'

export type EintragTyp = 'Setup' | 'News' | 'Einschätzung' | 'Fall' | 'Idee'

/** Ein Eintrag der Spalten Positiv und Negativ. */
export interface UebersichtEintrag {
  key: string
  typ: EintragTyp
  /** Die Zahl hinter dem Typ: „Dip 81", „+2", „GREEN", „Overhang 62", „im Markt". */
  kennzahl: string
  vorzeichen: 'positiv' | 'negativ'
  /** Wann es sich gerührt hat, ISO — für die Zeit in der Zeile und „neu". */
  zeit: string
  /** Statt der Zeit, wo es keine Uhrzeit gibt: „heute" beim Tagesbericht. */
  zeitText?: string
  neu: boolean
  titel: string
  detail: string
  /** Worauf es sich bezieht — Titel, Branche, Region. */
  ziel: string
  /** Wohin der Klick führt. */
  to: string
}

/** Ein Eintrag der Spalte Fällig. */
export interface FaelligEintrag {
  key: string
  typ: 'Aufgabe' | 'Ereignis'
  /** „Überfällig", „Heute", „Mo 08.09.", „wöchentlich", „bei Ereignis". */
  wann: string
  ueberfaellig: boolean
  heute: boolean
  /** „P1" bis „P3" bei Aufgaben, sonst leer. */
  prio: string
  titel: string
  ziel: string
  to: string
  neu: boolean
  /** Zum Ordnen: der Tag als JJJJ-MM-TT, ohne Termin ans Ende. */
  tag: string | null
}

/**
 * Die zwei Zahlen, ab denen etwas in die Spalten darf.
 *
 * Ein Setup zählt erst ab diesem Dip-Score, ein Fall erst ab diesem
 * Overhang. Darunter bleibt es weg — sonst wäre die Seite voll mit Halbem.
 */
export interface Schwellen {
  dip: number
  overhang: number
}

export const SCHWELLEN: Schwellen = { dip: 70, overhang: 50 }

/** Der Bezug der Marktlage: die eine Einschätzung über alles. */
export const MARKT_REF = 'mkt:IW:GLOBAL'

/** Was die Übersicht von einer Einschätzung braucht — die Ampel, nicht der Report. */
export type AmpelEinschaetzung = Pick<Assessment, 'id' | 'rating' | 'headline'
  | 'note' | 'targets' | 'assessed_at' | 'confirmed_at' | 'source' | 'created'>

/** Was die Übersicht von einer Zeile des Daily Stretch braucht. */
export type StretchZeile = Pick<Assessment, 'isin' | 'name' | 'exchange'
  | 'dip_score' | 'vetos' | 'summary' | 'status'>

/**
 * Seit wann etwas „neu" ist: gestern 18:00, in Ortszeit.
 *
 * FEST, nicht „die letzten 24 Stunden": Wer morgens die Übersicht öffnet,
 * will sehen, was seit Feierabend dazukam — und um 11 Uhr dieselbe Menge
 * wie um 9.
 */
export function neuSeit(jetzt: Date = new Date()): Date {
  const gestern = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() - 1, 18, 0, 0)
  return gestern
}

/** JJJJ-MM-TT in Ortszeit. */
export function alsTag(datum: Date): string {
  const zwei = (n: number) => String(n).padStart(2, '0')
  return `${datum.getFullYear()}-${zwei(datum.getMonth() + 1)}-${zwei(datum.getDate())}`
}

function istNeu(iso: string | null | undefined, seit: Date): boolean {
  return Boolean(iso) && new Date(iso!).getTime() >= seit.getTime()
}

function namen(refs: Ref[], leer = 'ohne Bezug'): string {
  return refs.length ? refs.map((r) => r.name ?? r.ref).join(' · ') : leer
}

const zahl = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

function betrag(geld: Money | null): string {
  return geld ? `${zahl.format(Number(geld.amount))} ${geld.currency}` : '—'
}

/** Der erste Satz, höchstens 140 Zeichen — mehr ist ein Absatz, kein Detail. */
function ersterSatz(text: string): string {
  const satz = text.split(/(?<=[.!?])\s/)[0] ?? text
  return satz.length > 140 ? `${satz.slice(0, 137).trimEnd()} …` : satz
}

const wochentagKurz = new Intl.DateTimeFormat('de-DE', { weekday: 'short' })

/** „Mo 08.09." */
function tagMitWochentag(iso: string): string {
  const [j, m, t] = iso.split('-').map(Number)
  const d = new Date(j, m - 1, t)
  return `${wochentagKurz.format(d).replace('.', '')} ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}.`
}

// --------------------------------------------------------------- Setups

/**
 * Aus dem Daily Stretch: Setups über der Dip-Schwelle.
 *
 * Ohne Veto positiv, mit Veto negativ — ein hoher Dip-Score mit Veto ist die
 * Warnung, nicht die Gelegenheit.
 */
export function ausStretch(bericht: Pick<Report, 'rows' | 'trading_date'>,
                           schwellen: Schwellen, heute: string): UebersichtEintrag[] {
  const tag = bericht.trading_date
  const neu = tag === heute
  const zeit = tag ? `${tag}T09:00:00` : new Date().toISOString()
  const zeitText = tag === heute ? 'heute' : tag ? tagMitWochentag(tag) : undefined
  const eintraege: UebersichtEintrag[] = []
  for (const zeile of bericht.rows as StretchZeile[]) {
    const dip = zeile.dip_score
    if (dip === null || dip < schwellen.dip) continue
    const titel = zeile.name ?? zeile.isin ?? '—'
    const ziel = [zeile.exchange, zeile.status].filter(Boolean).join(' · ')
    if (zeile.vetos.length === 0) {
      eintraege.push({
        key: `setup-${zeile.isin}`, typ: 'Setup', kennzahl: `Dip ${dip}`,
        vorzeichen: 'positiv', zeit, zeitText, neu, titel,
        detail: zeile.summary, ziel, to: '/reports/daily-stretch',
      })
    } else {
      eintraege.push({
        key: `veto-${zeile.isin}`, typ: 'Setup', kennzahl: 'Veto',
        vorzeichen: 'negativ', zeit, zeitText, neu, titel,
        detail: `Dip ${dip}, aber Veto: ${zeile.vetos.join(', ')}`, ziel,
        to: '/reports/daily-stretch',
      })
    }
  }
  return eintraege
}

// ----------------------------------------------------------------- News

/** Aus den Meldungen: das Vorzeichen ist die Bewertung. Null bleibt weg. */
export function ausNews(items: NewsItem[], seit: Date): UebersichtEintrag[] {
  return items.filter((n) => n.sentiment !== 0).map((n) => {
    const quelle = n.sources[0]?.publisher
    return {
      key: `news-${n.id}`, typ: 'News',
      kennzahl: n.sentiment > 0 ? `+${n.sentiment}` : `−${Math.abs(n.sentiment)}`,
      vorzeichen: n.sentiment > 0 ? 'positiv' : 'negativ',
      zeit: n.published_at, neu: istNeu(n.published_at, seit),
      titel: n.headline,
      detail: quelle ? `${ersterSatz(n.body)} · ${quelle}` : ersterSatz(n.body),
      ziel: namen(n.targets),
      to: n.targets[0] ? `/news?ref=${encodeURIComponent(n.targets[0].ref)}` : '/news',
    }
  })
}

// -------------------------------------------------------- Einschätzungen

/**
 * Aus den Einschätzungen: GREEN positiv, RED negativ. AMBER steht nur in der
 * Fußzeile — es ist kein Vorzeichen. Die Marktlage hat ihre eigene Zeile.
 */
export function ausEinschaetzungen(items: AmpelEinschaetzung[], seit: Date): UebersichtEintrag[] {
  return items
    .filter((e) => e.rating !== 'AMBER' && e.targets[0]?.ref !== MARKT_REF)
    .map((e) => ({
      key: `einschaetzung-${e.id}`, typ: 'Einschätzung', kennzahl: e.rating,
      vorzeichen: e.rating === 'GREEN' ? 'positiv' : 'negativ',
      zeit: e.assessed_at, neu: istNeu(e.assessed_at, seit),
      titel: e.headline,
      detail: `${e.confirmed_at ? `Bestätigt ${nurUhr(e.confirmed_at)}` : 'unbestätigt'} · ${e.source}`,
      ziel: namen(e.targets),
      to: e.targets[0] ? `/assessments?ref=${encodeURIComponent(e.targets[0].ref)}` : '/assessments',
    }))
}

// ---------------------------------------------------------------- Fälle

/**
 * Aus den Fällen: die Handelswirkung entscheidet.
 *
 * POSITIVE ist positiv. NEGATIVE und CAUTION sind negativ — aber erst ab der
 * Overhang-Schwelle, weil ein Fall mit kleiner Last kein Signal ist. Ein
 * abgeschlossener Fall trägt sein Ende als Kennzahl.
 */
export function ausFaellen(items: ResearchCase[], schwellen: Schwellen,
                           seit: Date): UebersichtEintrag[] {
  const eintraege: UebersichtEintrag[] = []
  for (const fall of items) {
    const beendet = fall.status.startsWith('RESOLVED_')
    let vorzeichen: 'positiv' | 'negativ' | null = null
    let kennzahl = ''
    if (beendet) {
      vorzeichen = fall.status === 'RESOLVED_POSITIVE' ? 'positiv'
        : fall.status === 'RESOLVED_NEGATIVE' ? 'negativ' : null
      kennzahl = 'geschlossen'
    } else if (fall.trading_impact === 'POSITIVE') {
      vorzeichen = 'positiv'
      kennzahl = fall.overhang_score !== null ? `Overhang ${fall.overhang_score}` : 'positiv'
    } else if ((fall.trading_impact === 'NEGATIVE' || fall.trading_impact === 'CAUTION')
               && fall.overhang_score !== null && fall.overhang_score >= schwellen.overhang) {
      vorzeichen = 'negativ'
      kennzahl = `Overhang ${fall.overhang_score}`
    }
    if (!vorzeichen) continue
    const teile = [label('case_type', fall.case_type)]
    if (fall.probability !== null) teile.push(`Wahrscheinlichkeit ${fall.probability} %`)
    teile.push(`Trading ${label('trading_impact', fall.trading_impact)}`)
    eintraege.push({
      key: `fall-${fall.id}`, typ: 'Fall', kennzahl, vorzeichen,
      zeit: fall.last_material_update_at ?? fall.updated ?? fall.created,
      neu: istNeu(fall.updated ?? fall.created, seit),
      titel: fall.title, detail: teile.join(' · '),
      ziel: fall.instruments.length
        ? fall.instruments.map((i) => i.name).join(' · ') : 'ohne Titel',
      to: `/research/${fall.id}`,
    })
  }
  return eintraege
}

// ---------------------------------------------------------------- Ideen

/**
 * Aus den Handelsideen: der Kurs gegen das Band.
 *
 * Im Einstiegsband ist positiv, unter der Ungültigkeitsgrenze negativ.
 * Dazwischen ist nichts zu melden — und Vorgeschlagenes zählt nur in der
 * Fußzeile.
 */
export function ausIdeen(items: TradeIdea[], seit: Date): UebersichtEintrag[] {
  const eintraege: UebersichtEintrag[] = []
  for (const idee of items) {
    if (idee.status !== 'ACTIVE' && idee.status !== 'ENTERED') continue
    const kurs = idee.last_price ? Number(idee.last_price.amount) : null
    if (kurs === null) continue
    const unten = idee.entry_low ? Number(idee.entry_low.amount) : null
    const oben = idee.entry_high ? Number(idee.entry_high.amount) : null
    const grenze = idee.invalid_below ? Number(idee.invalid_below.amount) : null
    const basis = {
      typ: 'Idee' as const, titel: idee.headline, ziel: idee.name,
      zeit: idee.updated ?? idee.created, neu: istNeu(idee.updated ?? idee.created, seit),
      to: `/trade-ideas?isin=${idee.isin}`,
    }
    if (grenze !== null && kurs < grenze) {
      eintraege.push({
        ...basis, key: `idee-${idee.id}`, kennzahl: 'unter Limit', vorzeichen: 'negativ',
        detail: `${betrag(idee.last_price)} unter der Grenze ${betrag(idee.invalid_below)} · Idee prüfen`,
      })
    } else if (unten !== null && oben !== null && kurs >= unten && kurs <= oben) {
      eintraege.push({
        ...basis, key: `idee-${idee.id}`, kennzahl: IDEE_STATUS[idee.status] ?? idee.status,
        vorzeichen: 'positiv',
        detail: `${betrag(idee.last_price)} im Band ${betrag(idee.entry_low)} – ${betrag(idee.entry_high)}`,
      })
    }
  }
  return eintraege
}

// --------------------------------------------------------------- Fällig

/** Aus den Aufgaben: der Stichtag entscheidet über „Überfällig", „Heute" oder den Tag. */
export function ausAufgaben(items: DueAction[], heute: string): FaelligEintrag[] {
  return items.map((a) => {
    const tag = a.due_at
    const ueberfaellig = tag !== null && tag < heute
    const istHeute = tag === heute
    const wann = ueberfaellig ? 'Überfällig' : istHeute ? 'Heute'
      : tag ? tagMitWochentag(tag)
      : a.frequency ? label('frequency', a.frequency) : 'bei Ereignis'
    return {
      key: `aufgabe-${a.id}`, typ: 'Aufgabe', wann, ueberfaellig, heute: istHeute,
      prio: `P${a.priority}`,
      titel: a.description ?? label('action_type', a.action_type),
      ziel: a.case_title ?? '', to: a.case_id ? `/research/${a.case_id}` : '/research',
      neu: false, tag,
    }
  })
}

/** Aus den Ereignissen der nächsten Tage: Termine, die den Titeln gelten. */
export function ausEreignissen(items: MarketEvent[], heute: string,
                               seit: Date): FaelligEintrag[] {
  return items.filter((e) => e.date_from !== null).map((e) => ({
    key: `ereignis-${e.id}`, typ: 'Ereignis',
    wann: e.date_from === heute ? 'Heute' : tagMitWochentag(e.date_from!),
    ueberfaellig: false, heute: e.date_from === heute, prio: '',
    titel: e.title, ziel: `${namen(e.targets, 'Markt')} · ${artText(e)}`,
    to: '/events', neu: istNeu(e.created, seit), tag: e.date_from,
  }))
}

/** Überfälliges zuerst, dann nach Tag, ohne Termin ans Ende. */
export function ordneFaellig(items: FaelligEintrag[]): FaelligEintrag[] {
  return [...items].sort((a, b) => {
    if (a.tag === null || b.tag === null) return a.tag === null ? (b.tag === null ? 0 : 1) : -1
    return a.tag.localeCompare(b.tag) || a.prio.localeCompare(b.prio)
  })
}

/** Jüngstes zuerst. */
export function ordneEintraege(items: UebersichtEintrag[]): UebersichtEintrag[] {
  return [...items].sort((a, b) => b.zeit.localeCompare(a.zeit))
}
